// Ported from the management UI's momentum chart (MomentumChart.js): the
// same rolling difference of each team's successful attacking passes
// (completed passes into the final third), just re-bucketed from a smooth
// SVG curve into a fixed pool of 90 bars, since PIXI has no charting library
// and the scene is built from 90 individual rectangles instead.
const ATTACKING_PASS_TYPE_ID = 1;
const THROW_IN_QUALIFIER_ID = 107;
const PASS_END_X_QUALIFIER_ID = 140;
const FINAL_THIRD_X = 66.6;
const HT_GAP_CHART_MINUTES = 3;
const BAR_COUNT = 90;
// Last 15 / First Half / Second Half all run on a physically smaller
// version of the graphic with half the bars.
const BAR_COUNT_HALF = 45;
const DEFAULT_WINDOW_MINS = 5;
const LAST_15_MINS = 15;
const MARKER_POOL_SIZE = 20;
// Straight red only (qualifier 33), matching this app's own card
// classification (Opta17.js) - a second yellow (qualifier 32) is a distinct
// type there, not folded into "red card".
const RED_CARD_QUALIFIER_ID = 33;

function isAttackingPass(e) {
  if (e?.typeId !== ATTACKING_PASS_TYPE_ID) return false;
  if (e?.outcome !== 1) return false;
  if (e?.qualifier?.some((q) => q?.qualifierId === THROW_IN_QUALIFIER_ID))
    return false;
  return (
    parseFloat(
      e?.qualifier?.find((q) => q?.qualifierId === PASS_END_X_QUALIFIER_ID)
        ?.value,
    ) > FINAL_THIRD_X
  );
}

// Only goals (regular and own), and red cards feed the marker pool -
// everything else (yellow cards, second yellows, subs) is ignored.
function buildMarker(e, homeId) {
  const isHomeSide = e.contestantId === homeId;
  if (e.typeId === 16) {
    const og = e.qualifier?.find((q) => q?.qualifierId === 28);
    const home = (isHomeSide && !og) || (!isHomeSide && og);
    return {
      type: og ? "og" : "goal",
      home,
      periodId: e.periodId,
      timeMin: e.timeMin,
      label: `${e.timeMin}' ${og ? "Own Goal" : "Goal"} - ${e.playerName || ""}`,
    };
  }
  if (
    e.typeId === 17 &&
    e.qualifier?.some((q) => q?.qualifierId === RED_CARD_QUALIFIER_ID)
  ) {
    return {
      type: "card",
      home: isHomeSide,
      periodId: e.periodId,
      timeMin: e.timeMin,
      colour: "#dc2626",
      label: `${e.timeMin}' Red - ${e.playerName || ""}`,
    };
  }
  return null;
}

export class MatchMomentum {
  constructor({ animate, preview, update, animateOff }) {
    this.onAir = false;
    this.sendAnimate = animate;
    this.sendAnimateOff = animateOff;
    this.sendPreview = preview;
    this.sendUpdate = update;
    this.name = "MatchMomentum";
    this.scene = "Match Momentum";
    this.data = [];
  }

  // Home/away attacking-pass counts per period, keyed by game-clock minute.
  buildPerMinuteCounts(events, homeId, awayId) {
    const per = { 1: { home: {}, away: {} }, 2: { home: {}, away: {} } };
    events?.forEach((e) => {
      if (!isAttackingPass(e)) return;
      if (e.periodId !== 1 && e.periodId !== 2) return;
      const side =
        e.contestantId === homeId
          ? "home"
          : e.contestantId === awayId
            ? "away"
            : null;
      if (!side) return;
      per[e.periodId][side][e.timeMin] =
        (per[e.periodId][side][e.timeMin] || 0) + 1;
    });
    return per;
  }

  // Rolling-average diff (home - away) for a single minute within a period.
  diffForMinute({ per, periodId, minute, startMin, windowMins }) {
    let h = 0;
    let a = 0;
    for (let k = Math.max(startMin, minute - windowMins) + 1; k <= minute; k++) {
      h += per[periodId].home[k] || 0;
      a += per[periodId].away[k] || 0;
    }
    const denom = Math.min(windowMins, minute - startMin) || 1;
    return (h - a) / denom;
  }

  buildFullMatchBars({ events, per, windowMins }) {
    const firstMins = events
      ?.filter((e) => e.periodId === 1 && e.timeMin > 0)
      .map((e) => e.timeMin) || [];
    const secondMins = events
      ?.filter((e) => e.periodId === 2 && e.timeMin > 0)
      .map((e) => e.timeMin) || [];
    const firstEnd = Math.max(45, ...(firstMins.length ? firstMins : [45]));
    const secondEnd = Math.max(90, ...(secondMins.length ? secondMins : [90]));

    const points = [];
    for (let m = 0; m <= Math.round(firstEnd); m++) {
      points.push({
        cm: m,
        diff: this.diffForMinute({
          per,
          periodId: 1,
          minute: m,
          startMin: 0,
          windowMins,
        }),
      });
    }
    const totalX = firstEnd + HT_GAP_CHART_MINUTES + (secondEnd - 45);
    if (secondMins.length > 0) {
      for (let m = 45; m <= Math.round(secondEnd); m++) {
        points.push({
          cm: firstEnd + HT_GAP_CHART_MINUTES + (m - 45),
          diff: this.diffForMinute({
            per,
            periodId: 2,
            minute: m,
            startMin: 45,
            windowMins,
          }),
        });
      }
    }

    const sums = new Array(BAR_COUNT).fill(0);
    const counts = new Array(BAR_COUNT).fill(0);
    points.forEach((p) => {
      let idx = Math.min(
        BAR_COUNT - 1,
        Math.max(0, Math.floor((p.cm / totalX) * BAR_COUNT)),
      );
      sums[idx] += p.diff;
      counts[idx] += 1;
    });
    const bars = sums.map((s, i) => (counts[i] ? s / counts[i] : 0));

    const cmForEvent = (e) =>
      e.periodId === 1 ? e.timeMin : firstEnd + HT_GAP_CHART_MINUTES + (e.timeMin - 45);
    const barIndexForEvent = (e) =>
      Math.min(
        BAR_COUNT - 1,
        Math.max(0, Math.floor((cmForEvent(e) / totalX) * BAR_COUNT)),
      );

    return { bars, barIndexForEvent };
  }

  buildLast15Bars({ per, events, windowMins }) {
    const allMins = events
      ?.filter((e) => e.periodId === 1 || e.periodId === 2)
      .map((e) => ({ periodId: e.periodId, timeMin: e.timeMin })) || [];
    const latest = allMins.reduce(
      (acc, e) =>
        !acc || e.periodId > acc.periodId || (e.periodId === acc.periodId && e.timeMin > acc.timeMin)
          ? e
          : acc,
      null,
    );
    const periodId = latest?.periodId || 1;
    const currentMin = latest?.timeMin || 0;
    const periodStart = periodId === 1 ? 0 : 45;
    const windowStart = Math.max(periodStart, currentMin - (LAST_15_MINS - 1));

    const perBarMinute = BAR_COUNT_HALF / LAST_15_MINS; // 3 bars per minute
    const bars = new Array(BAR_COUNT_HALF).fill(0);
    // Clamp so interpolating one minute past "now" doesn't reach into the
    // future - the last minute just holds flat instead.
    const diffAt = (m) =>
      this.diffForMinute({
        per,
        periodId,
        minute: Math.max(periodStart, Math.min(currentMin, m)),
        startMin: periodStart,
        windowMins,
      });
    // Right-align: the most recent minute always lands in the final slots,
    // so an early match (less than 15 minutes played) leaves the front of
    // the pool at zero rather than stretching what little data exists.
    for (let m = windowStart; m <= currentMin; m++) {
      const slot = LAST_15_MINS - 1 - (currentMin - m);
      const startBar = slot * perBarMinute;
      const d0 = diffAt(m);
      const d1 = diffAt(m + 1); // slight interpolation toward the next minute
      for (let sub = 0; sub < perBarMinute; sub++) {
        const t = (sub + 0.5) / perBarMinute;
        bars[startBar + sub] = d0 + (d1 - d0) * t;
      }
    }

    const barIndexForEvent = (e) => {
      if (e.periodId !== periodId) return -1;
      if (e.timeMin < windowStart || e.timeMin > currentMin) return -1;
      const slot = LAST_15_MINS - 1 - (currentMin - e.timeMin);
      return Math.min(BAR_COUNT_HALF - 1, slot * perBarMinute);
    };

    return { bars, barIndexForEvent, from: windowStart, to: currentMin };
  }

  // First/second half, bucketed onto BAR_COUNT_HALF the same way the full
  // match buckets onto BAR_COUNT - scoped to one period, including stoppage.
  buildHalfBars({ events, per, windowMins, periodId }) {
    const periodStart = periodId === 1 ? 0 : 45;
    const defaultEnd = periodId === 1 ? 45 : 90;
    const periodMins = events
      ?.filter((e) => e.periodId === periodId && e.timeMin > periodStart)
      .map((e) => e.timeMin) || [];
    const periodEnd = Math.max(defaultEnd, ...(periodMins.length ? periodMins : [defaultEnd]));
    const totalX = periodEnd - periodStart;

    const points = [];
    for (let m = periodStart; m <= Math.round(periodEnd); m++) {
      points.push({
        cm: m - periodStart,
        diff: this.diffForMinute({
          per,
          periodId,
          minute: m,
          startMin: periodStart,
          windowMins,
        }),
      });
    }

    const sums = new Array(BAR_COUNT_HALF).fill(0);
    const counts = new Array(BAR_COUNT_HALF).fill(0);
    points.forEach((p) => {
      let idx = Math.min(
        BAR_COUNT_HALF - 1,
        Math.max(0, Math.floor((p.cm / totalX) * BAR_COUNT_HALF)),
      );
      sums[idx] += p.diff;
      counts[idx] += 1;
    });
    const bars = sums.map((s, i) => (counts[i] ? s / counts[i] : 0));

    const barIndexForEvent = (e) => {
      if (e.periodId !== periodId) return -1;
      const cm = e.timeMin - periodStart;
      return Math.min(
        BAR_COUNT_HALF - 1,
        Math.max(0, Math.floor((cm / totalX) * BAR_COUNT_HALF)),
      );
    };

    return {
      bars,
      barIndexForEvent,
      from: periodStart,
      to: Math.round(periodEnd),
    };
  }

  preview({ options }) {
    this.onAir = false;
    this.data = [];

    const { game, opta_events, last15, half, windowMins } = options;
    const homeId = game?.home_team?.opta_ID;
    const awayId = game?.away_team?.opta_ID;
    const window = windowMins || DEFAULT_WINDOW_MINS;

    const per = this.buildPerMinuteCounts(opta_events, homeId, awayId);

    // Last 15 / First Half / Second Half share one "Match Momentum Half"
    // scene rather than each getting their own - a Variant field on that
    // scene ("15"/"1st"/"2nd") tells the template which one this is.
    let bars;
    let barIndexForEvent;
    let variant = "";
    let axisFrom = "";
    let axisTo = "";
    if (last15) {
      variant = "15";
      this.scene = "Match Momentum Half";
      let from, to;
      ({ bars, barIndexForEvent, from, to } = this.buildLast15Bars({
        per,
        events: opta_events,
        windowMins: window,
      }));
      axisFrom = Math.round(from) + "'";
      axisTo = Math.round(to) + "'";
    } else if (half === 1) {
      variant = "1st";
      this.scene = "Match Momentum Half";
      let from, to;
      ({ bars, barIndexForEvent, from, to } = this.buildHalfBars({
        events: opta_events,
        per,
        windowMins: window,
        periodId: 1,
      }));
      axisFrom = Math.round(from) + "'";
      axisTo = Math.round(to) + "'";
    } else if (half === 2) {
      variant = "2nd";
      this.scene = "Match Momentum Half";
      let from, to;
      ({ bars, barIndexForEvent, from, to } = this.buildHalfBars({
        events: opta_events,
        per,
        windowMins: window,
        periodId: 2,
      }));
      axisFrom = Math.round(from) + "'";
      axisTo = Math.round(to) + "'";
    } else {
      this.scene = "Match Momentum";
      ({ bars, barIndexForEvent } = this.buildFullMatchBars({
        events: opta_events,
        per,
        windowMins: window,
      }));
    }

    let markers = [];
    opta_events?.forEach((e) => {
      const marker = buildMarker(e, homeId);
      if (!marker) return;
      const barIndex = barIndexForEvent(e);
      if (barIndex < 0) return;
      markers.push({ ...marker, barIndex });
    });
    markers = markers.slice(0, MARKER_POOL_SIZE);

    this.data.push({
      name: "Variant",
      value: { text: variant },
    });
    this.data.push({
      name: "Axis From",
      value: { text: axisFrom },
    });
    this.data.push({
      name: "Axis To",
      value: { text: axisTo },
    });
    this.data.push({
      name: "Momentum",
      value: { text: JSON.stringify(bars) },
    });
    this.data.push({
      name: "Momentum Markers",
      value: { text: JSON.stringify(markers) },
    });

    this.data.push({
      name: "Competition",
      value: { text: game?.competition?.name || "" },
    });
    this.data.push({
      name: "Competition Colour",
      value: { colour: game?.competition?.colour || "" },
    });
    this.data.push({
      name: "Competition Badge",
      value: { image: "${user}" + (game?.competition?.badge || "") },
    });

    this.data.push({
      name: "Home Team Name",
      value: { text: game?.home_team?.name || "" },
    });
    this.data.push({
      name: "Away Team Name",
      value: { text: game?.away_team?.name || "" },
    });
    this.data.push({
      name: "Home Team Badge",
      value: { image: "${user}" + (game?.home_team?.variables?.badge || "") },
    });
    this.data.push({
      name: "Away Team Badge",
      value: { image: "${user}" + (game?.away_team?.variables?.badge || "") },
    });
    this.data.push({
      name: "Home Team Kit Colour",
      value: {
        colour:
          game?.home_team?.variables?.[game?.home_team_kit + "_kit_bg"] || "",
      },
    });
    this.data.push({
      name: "Away Team Kit Colour",
      value: {
        colour:
          game?.away_team?.variables?.[game?.away_team_kit + "_kit_bg"] || "",
      },
    });

    this.data.push({
      name: "Home Team Code",
      value: { text: options.game?.home_team?.code || "" },
    });
    this.data.push({
      name: "Away Team Code",
      value: { text: options.game?.away_team?.code || "" },
    });

    this.sendPreview(this.scene, 500, this.data, "IN");
  }

  animate({ options }) {
    this.onAir = true;
    this.sendAnimate(this.scene, this.data);
  }
  animateOff({ options }) {
    this.onAir = false;
    this.sendAnimateOff(this.scene, []);
  }
}
